/**
 * Google Analytics 4 — the pure half (decisions.md D12). The portal record's
 * `googleAnalyticId` turns it on per portal; the theme never names a property.
 *
 * Basic consent mode: Google's script is not even requested before the visitor
 * accepts, so nothing reaches googletagmanager.com or google-analytics.com
 * pre-consent. The Consent Mode v2 defaults are still set first (everything
 * denied), and acceptance grants `analytics_storage` alone — ad signals stay
 * denied, this theme runs no Google ads. Page views are GA's own: the
 * config's first one plus enhanced measurement's history-change page views for
 * App Router navigation, so the theme sends none (sending them too would count
 * every page twice). Client wiring: components/analytics/GoogleAnalytics.tsx.
 */

/** A GA4 measurement ID (`G-` + its alphanumeric suffix). Anything else — a
 *  retired `UA-` property, a GTM container, a typo — loads nothing. */
export function isGaMeasurementId(id: string | null | undefined): id is string {
  return typeof id === "string" && /^G-[A-Z0-9]{4,16}$/.test(id);
}

export const GA_SCRIPT_URL = "https://www.googletagmanager.com/gtag/js";

export const GA_CONSENT_DEFAULTS = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

/**
 * The domains GA may have written `_ga` / `_ga_<id>` on: GA's `cookie_domain:
 * "auto"` picks the highest domain it can set a cookie on, so a withdrawal
 * clears the host and every parent above it (`media.n24.si` → `media.n24.si`,
 * `n24.si`; the bare TLD is never a cookie domain).
 */
export function gaCookieDomains(hostname: string): string[] {
  const parts = hostname.split(".");
  if (parts.length < 2 || /^[\d.]+$/.test(hostname)) return [hostname];
  return parts.slice(0, -1).map((_, i) => parts.slice(i).join("."));
}

/** Whether a cookie name is one of GA's (`_ga`, `_ga_<container>`). */
export function isGaCookie(name: string): boolean {
  return name === "_ga" || name.startsWith("_ga_");
}
